import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import type { Lang, ModeId } from '@polyglot/engine';
import { isModeId, LANGS, MODES, sortLangs, validateLanguageSelection } from '@polyglot/engine';
import { BackLink, LangName, Spinner } from '../components/ui';
import { api, ApiError, ensureSession } from '../lib/api';
import { useSettings } from '../lib/settings';
import { ContractPreview, LanguagePicker } from './Setup';

type ChallengeMode = Exclude<ModeId, 'relax'>;

interface Created {
  matchId: string;
  legs: string[];
  token: string;
  inviteUrl: string;
  expiresAt: string;
}

export function ChallengeNew({ params }: { params: { mode?: string } }) {
  const { t, i18n } = useTranslation();
  const settings = useSettings();
  const initial: ChallengeMode =
    isModeId(params.mode) && params.mode !== 'relax' ? params.mode : 'classic';
  const [mode, setMode] = useState<ChallengeMode>(initial);
  const def = MODES[mode];
  const [langs, setLangs] = useState<Lang[]>(settings.lastLanguages.slice(0, def.maxLanguages));
  const [returnMatch, setReturnMatch] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Created | null>(null);
  const [copied, setCopied] = useState(false);
  const selectionError = validateLanguageSelection(def, langs);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      await ensureSession(settings.uiLang);
      const to = new URLSearchParams(location.search).get('to') ?? undefined;
      const r = await api<Created>('POST', '/api/challenges', {
        inviteeId: to,
        mode,
        languages: langs,
        returnMatch: returnMatch && def.languagePolicy === 'shared',
      });
      setCreated(r);
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === 'no_draw_available'
          ? t('setup.noDraw')
          : t('errors.generic'),
      );
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    const shareText = t('challenge.shareText', { mode: t(`modes.${mode}.name`) });
    return (
      <main className="page">
        <BackLink />
        <h1>{t('challenge.newTitle')}</h1>
        <section className="card">
          <p>{t('challenge.created')}</p>
          <p className="invite-url">
            <code>{created.inviteUrl}</code>
          </p>
          <p className="muted small">
            {t('challenge.expires', {
              date: new Date(created.expiresAt).toLocaleString(i18n.language),
            })}
          </p>
          <div className="row wrap">
            <button
              className="btn primary"
              onClick={async () => {
                try {
                  if (navigator.share)
                    await navigator.share({ text: shareText, url: created.inviteUrl });
                  else {
                    await navigator.clipboard.writeText(created.inviteUrl);
                    setCopied(true);
                  }
                } catch {
                  /* cancelled */
                }
              }}
            >
              {t('challenge.share')}
            </button>
            <button
              className="btn ghost"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(created.inviteUrl);
                  setCopied(true);
                } catch {
                  /* clipboard unavailable */
                }
              }}
            >
              {copied ? t('common.copied') : t('common.copy')}
            </button>
          </div>
        </section>
        <Link className="btn primary big" href={`/match/${created.legs[0]}`}>
          {t('challenge.playMine')}
        </Link>
      </main>
    );
  }

  return (
    <main className="page">
      <BackLink />
      <h1>{t('challenge.newTitle')}</h1>
      <fieldset className="mode-picker">
        <legend>{t('setup.mode')}</legend>
        {(['classic', 'polyglot', 'duel', 'sprint'] as const).map((m) => (
          <button
            type="button"
            key={m}
            className={`mode-card${m === mode ? ' selected' : ''}`}
            aria-pressed={m === mode}
            onClick={() => {
              setMode(m);
              const d = MODES[m];
              if (langs.length > d.maxLanguages) setLangs(langs.slice(0, d.maxLanguages));
            }}
          >
            <strong>{t(`modes.${m}.name`)}</strong>
            <span className="muted small">{t(`modes.${m}.short`)}</span>
          </button>
        ))}
      </fieldset>
      <LanguagePicker
        value={langs}
        onChange={setLangs}
        max={def.maxLanguages}
        label={
          def.languagePolicy === 'shared'
            ? t('setup.languagesShared', { min: def.minLanguages, max: def.maxLanguages })
            : t('setup.languagesIndividual')
        }
      />
      <ContractPreview mode={mode} langs={langs} />
      {def.languagePolicy === 'shared' && (
        <label className="row toggle">
          <input
            type="checkbox"
            checked={returnMatch}
            onChange={(e) => setReturnMatch(e.target.checked)}
          />
          {t('challenge.returnMatch')}
        </label>
      )}
      {error && (
        <p className="message error" role="alert">
          {error}
        </p>
      )}
      {busy ? (
        <Spinner label={t('setup.loading')} />
      ) : (
        <button
          className="btn primary big"
          disabled={!!selectionError}
          onClick={() => void create()}
        >
          {t('challenge.create')}
        </button>
      )}
    </main>
  );
}

interface Preview {
  matchId: string;
  mode: ChallengeMode;
  returnMatch: boolean;
  creatorName: string;
  sharedLanguages: Lang[] | null;
  compatible: string[] | null;
  expiresAt: string | null;
  status: 'open' | 'taken' | 'expired';
  isCreator: boolean;
  isParticipant: boolean;
}

export function ChallengeAccept({ params }: { params: { token: string } }) {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const settings = useSettings();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [langs, setLangs] = useState<Lang[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    ensureSession(settings.uiLang)
      .then(() => api<Preview>('GET', `/api/challenges/${params.token}`))
      .then(setPreview)
      .catch(() => setError(t('challenge.notFound')));
  }, [params.token, settings.uiLang, t]);

  const goFirstLeg = async (matchId: string, legs: string[]) => {
    if (legs.length <= 1) return navigate(`/match/${legs[0] ?? matchId}`);
    // Return match: the opponent starts with the other draw.
    const views = await Promise.all(
      legs.map((l) => api<{ legOrder: number | null }>('GET', `/api/matches/${l}`)),
    );
    const first = legs[views.findIndex((v) => v.legOrder === 0)] ?? legs[0]!;
    navigate(`/match/${first}`);
  };

  const accept = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const body = MODES[preview.mode].languagePolicy === 'individual' ? { languages: langs } : {};
      const r = await api<{ matchId: string; legs: string[] }>(
        'POST',
        `/api/challenges/${params.token}/accept`,
        body,
      );
      await goFirstLeg(r.matchId, r.legs);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'generic';
      setError(
        code === 'expired'
          ? t('challenge.expired')
          : code === 'challenge_taken'
            ? t('challenge.taken')
            : t('errors.generic'),
      );
      setBusy(false);
    }
  };

  if (error && !preview)
    return (
      <main className="page">
        <p className="message error">{error}</p>
        <Link href="/">{t('nav.home')}</Link>
      </main>
    );
  if (!preview) return <Spinner label={t('common.loading')} />;
  const def = MODES[preview.mode];
  const compatible = new Set(preview.compatible ?? []);
  const langsOk =
    def.languagePolicy === 'shared' ||
    (validateLanguageSelection(def, langs) === null && compatible.has(sortLangs(langs).join('+')));

  return (
    <main className="page">
      <h1>{t('challenge.inviteFrom', { name: preview.creatorName })}</h1>
      <section className="card">
        <p>
          <strong>{t(`modes.${preview.mode}.name`)}</strong> — {t(`modes.${preview.mode}.desc`)}
        </p>
        {preview.sharedLanguages && (
          <p>
            {preview.sharedLanguages.map((l) => (
              <LangName key={l} lang={l} />
            ))}
          </p>
        )}
        {preview.returnMatch && <p className="muted small">{t('challenge.returnInfo')}</p>}
      </section>
      {preview.isCreator ? (
        <p>{t('challenge.own')}</p>
      ) : preview.isParticipant ? (
        <button className="btn primary big" onClick={() => void accept()}>
          {t('challenge.goToMatch')}
        </button>
      ) : preview.status !== 'open' ? (
        <p className="message error">
          {preview.status === 'expired' ? t('challenge.expired') : t('challenge.taken')}
        </p>
      ) : (
        <>
          {def.languagePolicy === 'individual' && (
            <>
              <p className="muted small">{t('challenge.compatibleOnly')}</p>
              <fieldset className="lang-picker">
                <legend>{t('challenge.chooseLanguages')}</legend>
                {LANGS.map((l) => {
                  const selected = langs.includes(l);
                  const wouldBe = selected ? langs : [...langs, l];
                  const possible = [...compatible].some((c) =>
                    sortLangs(wouldBe).every((x) => c.split('+').includes(x)),
                  );
                  return (
                    <button
                      key={l}
                      type="button"
                      className={`chip big${selected ? ' selected' : ''}`}
                      aria-pressed={selected}
                      disabled={!selected && (!possible || langs.length >= def.maxLanguages)}
                      onClick={() =>
                        setLangs(selected ? langs.filter((x) => x !== l) : [...langs, l])
                      }
                    >
                      <LangName lang={l} />
                    </button>
                  );
                })}
              </fieldset>
              <ContractPreview mode={preview.mode} langs={langs} />
            </>
          )}
          {error && <p className="message error">{error}</p>}
          <button
            className="btn primary big"
            disabled={!langsOk || busy}
            onClick={() => void accept()}
          >
            {t('challenge.accept')}
          </button>
        </>
      )}
    </main>
  );
}
